// What the engine made of a game, as stored: the scan and its judgements,
// and how a game splits into stages. Running a scan is the `analysis` API's;
// reading the review's rules over it is `@chess/api-review`'s.
import type { Color, Fen, San, Uci } from './chess.ts';

/** An engine score for the side to move: centipawns, or moves to mate. */
export interface Score {
  cp?: number;
  /** Positive: the side to move mates in this many; zero: it is mated. */
  mate?: number;
}

/** One of the engine's lines from a position: its first move, its score, a short continuation. */
export interface Line {
  uci: Uci;
  score: Score;
  /** The line's moves including the first, at most `PV_LENGTH` plies. */
  pv: Uci[];
}

/** What the engine made of one position. `ply` 0 is the start. */
export interface PositionAnalysis {
  ply: number;
  fen: Fen;
  depth: number;
  /** The position's score, for the side to move: the best line's. */
  score: Score;
  /** The top lines, best first, at most `LINES`. Empty when the game is over here. */
  lines: Line[];
}

export type Judgement = 'best' | 'good' | 'inaccuracy' | 'mistake' | 'blunder';

/** How the move at a ply compares with the engine's choice. `ply` is 1-based: the move is `moves[ply - 1]`. */
export interface MoveJudgement {
  ply: number;
  uci: Uci;
  san: San;
  /** The engine's move from the position before. */
  best: Uci;
  /** Centipawns given up against the best line, never negative. */
  delta: number;
  judgement: Judgement;
}

/** A game's scan. Partial while `complete` is false; a partial scan is continued, never redone. */
export interface Scan {
  schemaVersion: 1;
  gameId: string;
  /** The engine that produced it, so a different one can replace it. */
  engine: string;
  /** The depth every position was searched to. */
  depth: number;
  /** How many positions the game has, start included. */
  total: number;
  /** Sorted by ply; may have gaps until complete. */
  positions: PositionAnalysis[];
  /** One per move, in order, filled once the scan completes. */
  moves: MoveJudgement[];
  complete: boolean;
  /** ISO 8601, last write. */
  updatedAt: string;
}


/** What the review lists of a move: a blunder, or a miss (a winning line passed over). */
export type MoveKind = 'blunder' | 'miss';

/** The app's one grade of a move (`moveGradeOf` in `@chess/api-review`): the user's rules decide blunder and miss, the scan's fixed steps the rest. */
export type MoveGrade = 'best' | 'good' | 'inaccuracy' | 'mistake' | 'blunder' | 'miss';

/** Best to worst, a miss last: the order of a legend. */
export const MOVE_GRADES: readonly MoveGrade[] = ['best', 'good', 'inaccuracy', 'mistake', 'blunder', 'miss'];

/** The three stages of a game. */
export type Phase = 'opening' | 'middlegame' | 'ending';

/** Where a game's stages split, decided once when it opens so every view agrees. */
export interface ReviewPhases {
  /** The last ply of the opening: the opening's moves are plies `1 … openingEnd`. */
  openingEnd: number;
  /** The first ply of the endgame, or undefined when the game never reached one. */
  endgameStart?: number;
  /** Index of the last position the opening book knows; undefined when no book read the game. */
  lastKnownPly?: number;
  /** Who played the move out of the book; undefined when the game never left it or no book read it. */
  leftBy?: Color;
}
